import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

/*Dependancy, Library and Module Imports*/
/*NB: Mind the order of NgModule imports.
Each new module must come after the BrowserModule.*/
import {BrowserModule} from '@angular/platform-browser';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import { MatListModule } from '@angular/material/list';

import {
  NgModule,
  Injectable,
  LOCALE_ID,
  TRANSLATIONS_FORMAT,
  APP_INITIALIZER,
  TRANSLATIONS,
  ErrorHandler
  // ErrorHandler
} from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG, OverlayContainer } from '@angular/cdk/overlay';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {TranslationLoaderService} from './core/services/translation-loader.service';

/*Component Imports*/
import {AppComponent} from './app.component';
import {CoreModule} from './core/core.module';
import {SharedModule} from './shared/shared.module';

import {environment} from './../environments/environment';
import {routing} from './app.routing';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AutoLogoutDialogComponent} from './auto-logout-dialog/auto-logout-dialog.component';
import {
  VerificationLevelZeroUpdateDialogComponent
} from './profile/verification-level-zero-update-dialog/verification-level-zero-update-dialog.component';
import {MarketConfirmationComponent} from './markets/market-confirmation/market-confirmation.component';

import {MetaModule} from '@ngx-meta/core';

import {HttpClientModule} from '@angular/common/http';

import {Angulartics2Module} from 'angulartics2';
import {ConfirmDialogComponent} from './shared/confirm-dialog/confirm-dialog.component';

// States
import { CompetitionsState } from './leader-board/state/competition.state';
import { AddressBookStore } from './balances/address-book/state/address-book.state';
import { ScullyLibModule } from '@scullyio/ng-lib';
import { EarnProductsState } from './balances/currency-management/earn-portal/state/earn-portal.state';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BankAddressBookComponent } from './balances/address-book/bank-address-book/bank-address-book.component';
import { AirbrakeErrorHandler } from './airbrake-client';
// import {AirbrakeErrorHandler} from './airbrake-client';

export function initTranslation(loader: TranslationLoaderService) {
  return () => loader.load();
}

@Injectable()
export class AppOverlayContainer extends OverlayContainer {
  protected override _createContainer(): void {
    super._createContainer();
    if (this._containerElement) {
      const storedTheme = sessionStorage.getItem('THEME') || 'dark-theme';
      const themeClass = storedTheme.includes('light') ? 'light-theme' : 'dark-theme';
      this._containerElement.classList.add(themeClass);
    }
  }
}

@NgModule({
    declarations: [
        AppComponent,
        AutoLogoutDialogComponent,
        VerificationLevelZeroUpdateDialogComponent,
        MarketConfirmationComponent,
        BankAddressBookComponent
    ],
    imports: [
        BrowserModule,
        CommonModule,
        HttpClientModule,
        routing,
        MatListModule,
        CoreModule,
        SharedModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MetaModule.forRoot(),
        Angulartics2Module.forRoot({
            pageTracking: {
                clearIds: true,
                clearQueryParams: true,
                clearHash: true,
                excludedRoutes: [
                    '.*\/confirm.*',
                    '.*\/authorise.*',
                    '.*\/.*verify.*'
                ],
            }
        }),
        NgxsModule.forRoot([CompetitionsState, AddressBookStore, EarnProductsState], {
            developmentMode: !environment.production
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        ScullyLibModule.forRoot({ useTransferState: false })
    ],
    providers: [
        provideAnimationsAsync(),
        {provide: ErrorHandler, useClass: AirbrakeErrorHandler},
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        { provide: OverlayContainer, useClass: AppOverlayContainer },
        // format of translations that you use, not needed if using AOT
        { provide: TRANSLATIONS_FORMAT, useValue: 'xlf' },
        // locale id that you're using (default en-US) ALWAYS NEEDED
        { provide: LOCALE_ID, useValue: environment.config.language },
        {
            provide: APP_INITIALIZER,
            useFactory: initTranslation,
            deps: [TranslationLoaderService],
            multi: true
        },
        {
            provide: TRANSLATIONS,
            useFactory: (loader: TranslationLoaderService) => {
                return loader.getTranslations() || '';
            },
            deps: [TranslationLoaderService]
        },
    ],
    bootstrap: [AppComponent],
    exports: []
})
export class AppModule {
  /*
  This call exports all data needed to authenticate and/or register a user
  */

}
